import { NextSeo } from 'next-seo';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Breadcrumb from '@/components/breadcrumb';
import HtmlContentContainer from '@/components/html-content-container';
import LayoutBase from '@/components/layout-base';
import contentLayoutBaseData from '@/content/content-layout-base-data';
import type { NewsArticleDetail } from '@/content/content-news-detail-data';
import { contentNewsDetailData } from '@/content/content-news-detail-data';
import contentNewsDetailUrl from '@/content/content-news-detail-url';
import { contentNewListDataArticles } from '@/content/content-news-list-data';
import contentNewsListUrl from '@/content/content-news-list-url';
import i18nMessagesLoad from '@/lib/i18n-messages-load';
import { localizationContextFromGetStaticPropsContext } from '@/lib/localization-contenxt';
import revalidateAfter from '@/lib/revalidate-after';
import type { NextGetStaticProps, NextPageWithLayout } from '../_app';
import type { GetStaticPaths } from 'next';

type NewsDetailPageUrlParams = { newsSlug: string };

export type NewsDetailPageProps = {
  article: NewsArticleDetail;
};

const NewsDetailPage: NextPageWithLayout<NewsDetailPageProps> = ({ article }) => {
  const intl = useIntl();

  const { body, metaData, slug, teaserText, title } = article;

  const metaTagKeyWords = metaData.keyWords ? [{ content: metaData.keyWords, property: 'keywords' }] : [];

  const parentPageTitle = useMemo(
    () =>
      intl.formatMessage({
        defaultMessage: 'Zprávy',
        description: 'Title of page News.',
        id: 'c733KF',
      }),
    [intl],
  );

  return (
    <>
      <NextSeo
        additionalMetaTags={metaTagKeyWords}
        description={metaData.description || teaserText || ''}
        title={`${metaData.title || title} | ${parentPageTitle}`}
      />
      <main className='mx-auto mb-8 max-w-7xl px-4 sm:px-6 lg:px-8'>
        <Breadcrumb className='py-4'>
          <Breadcrumb.Item href={contentNewsListUrl()} isCurrenctPage={false} position={1}>
            {parentPageTitle}
          </Breadcrumb.Item>
          <Breadcrumb.Item href={contentNewsDetailUrl(slug)} isCurrenctPage={true} position={2}>
            {title}
          </Breadcrumb.Item>
        </Breadcrumb>

        <div className='mx-auto max-w-prose text-lg'>
          <h1 className='heading-page'>{title}</h1>
          <HtmlContentContainer
            applyDefaultClassName={false}
            as='section'
            className='prose prose-lg mx-auto mt-6'
            html={body}
          />
        </div>
      </main>
    </>
  );
};

NewsDetailPage.displayName = 'NewsDetailPage';

NewsDetailPage.getLayout = (page) => {
  return <LayoutBase>{page}</LayoutBase>;
};

export default NewsDetailPage;

// *****************************************
// *
// * getStaticProps
// *
// *****************************************

export const getStaticProps: NextGetStaticProps<NewsDetailPageProps, NewsDetailPageUrlParams> = async (
  getStaticPropsContext,
) => {
  if (getStaticPropsContext.params?.newsSlug == null) return { notFound: true };
  const localizationContext = localizationContextFromGetStaticPropsContext(getStaticPropsContext);
  const intlMessages = await i18nMessagesLoad(localizationContext);
  const layoutData = await contentLayoutBaseData(localizationContext);
  const newsDetailPageData = await contentNewsDetailData(localizationContext, getStaticPropsContext.params.newsSlug);
  if (newsDetailPageData == null) return { notFound: true, revalidate: revalidateAfter('1h') };

  return {
    props: {
      ...newsDetailPageData,
      intlMessages,
      layoutData,
    },
    revalidate: revalidateAfter('1h'),
  };
};

// *****************************************
// *
// * getStaticProps
// *
// *****************************************

export const getStaticPaths: GetStaticPaths<NewsDetailPageUrlParams> = async ({ locales }) => {
  const articles: { locale: string; slug: string }[] = [];

  const overLocales = locales ?? ['cs'];
  for (const locale of overLocales) {
    const data = await contentNewListDataArticles({ locale, region: 'CZ' });
    data.forEach((a) => {
      articles.push({ locale, slug: a.slug });
    });
  }

  return {
    fallback: 'blocking',
    paths: articles.map((a) => ({
      locale: locales == null ? undefined : a.locale,
      params: {
        newsSlug: a.slug,
      },
    })),
  };
};
